import React from 'react';
import {
  Typography,
  Layout,
  Row,
  Col,
  Switch,
  Divider,
  Flex,
  Button,
  List,
} from 'antd';

import { localStorage } from '../../utils/namespaced-storage';

const { Header, Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const developerStyle = {
  innerLayout: {
    width: '100%',
    padding: '0 1rem 2rem',
    height: 'max-content',
    minHeight: '100%',
  },
  header: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2.4rem 0',
    background: 'none',
    height: 'auto',
  },
  headerTitle: {
    marginBottom: 0,
    marginTop: 0,
    fontWeight: 400,
  },
  headerParagraph: {
    fontWeight: 400,
    whiteSpace: 'wrap',
  },
  content: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
    background: '#ffffff',
    borderRadius: '4px 4px 0 0',
    border: '1px solid rgb(229, 231, 233)',
  },
  contentTable: {
    width: '100%',
  },
  divider: {
    margin: '1rem 0',
  },
  button: {
    width: '20rem',
  },
};

const betaToggles = {
  notifications: {
    key: 'betaToggleAccessNotifications',
  },
  shorePayOnline: {
    key: 'betaToggleAccessShorePayOnlineFeatures',
  },
  mainMenu: {
    key: 'betaToggleAccessMainMenu',
  },
};

export function Beta() {
  const hasEnabledBetaToggleAccessNotifications =
    localStorage?.getItem(betaToggles.notifications.key) === 'on' || false;
  const hasEnabledbetaToggleAccessShorePayOnlineFeatures =
    localStorage?.getItem(betaToggles.shorePayOnline.key) === 'on' || false;
  const hasEnabledBetaToggleAccessMainMenu =
    localStorage?.getItem(betaToggles.mainMenu.key) === 'on' || false;

  const handleOnBetaToggleAccessNotificationsChange = (checked: boolean) => {
    if (checked) {
      localStorage.setItem(betaToggles.notifications.key, 'on');
    } else {
      localStorage.removeItem(betaToggles.notifications.key);
      localStorage.removeItem(betaToggles.shorePayOnline.key);
      localStorage.removeItem(betaToggles.mainMenu.key);
    }
    window.location.reload();
  };

  const handleOnBetaToggleAccessMainMenuChange = (checked: boolean) => {
    if (checked) {
      localStorage.setItem(betaToggles.mainMenu.key, 'on');
    } else {
      localStorage.removeItem(betaToggles.mainMenu.key);
    }
    window.location.reload();
  };

  const handleOnBetaToggleAccessShorePayOnlineFeaturesChange = (
    checked: boolean
  ) => {
    if (checked) {
      localStorage.setItem(betaToggles.shorePayOnline.key, 'on');
    } else {
      localStorage.removeItem(betaToggles.shorePayOnline.key);
    }
    window.location.reload();
  };

  return (
    <Layout style={developerStyle.innerLayout}>
      <Header style={developerStyle.header}>
        <Title level={3} style={developerStyle.headerTitle}>
          Beta Features
        </Title>
        <Paragraph type="secondary" style={developerStyle.headerParagraph}>
          This page offers access to experimental features in beta testing,
          giving Beta Testing Program participants early access to new features
          before their official release.
        </Paragraph>
      </Header>
      <Content style={developerStyle.content}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col span={16}>
            <Title level={4} style={{ marginTop: 0 }}>
              Beta Access Notifications
            </Title>
            <Paragraph type="secondary" style={developerStyle.headerParagraph}>
              Enable this option to receive notifications whenever beta features
              become available in specific parts of the application. Once
              enabled, you’ll start seeing in-app alerts when new beta features
              are ready for testing.
            </Paragraph>
          </Col>
          <Col span={8}>
            <Flex gap={16} align="end" vertical>
              <Flex align="center" gap={16}>
                <Text>Show Beta Notifications</Text>
                <Switch
                  checkedChildren="On"
                  unCheckedChildren="Off"
                  defaultChecked={hasEnabledBetaToggleAccessNotifications}
                  onChange={handleOnBetaToggleAccessNotificationsChange}
                />
              </Flex>
            </Flex>
          </Col>
          <Col span={16}>
            <Title level={4} style={{ marginTop: 0 }}>
              Share Your Feedback
            </Title>
            <Paragraph type="secondary" style={developerStyle.headerParagraph}>
              Your feedback helps us improve beta features before their official
              release. Let us know what you think!
            </Paragraph>
          </Col>
          <Col span={8}>
            <Flex gap={16} align="end" vertical>
              <Button type="primary" disabled style={developerStyle.button}>
                Share Feedback
              </Button>
            </Flex>
          </Col>
          <Divider style={developerStyle.divider} />
          {hasEnabledBetaToggleAccessNotifications && (
            <Col span={24}>
              <Row>
                <Col span={16}>
                  <Title level={4} style={{ marginTop: 0 }}>
                    Beta Features Available for Testing
                  </Title>
                  <Paragraph
                    type="secondary"
                    style={developerStyle.headerParagraph}
                  >
                    Explore the latest beta features available for testing. Help
                    us improve these features by trying them out and providing
                    feedback.
                  </Paragraph>
                </Col>
              </Row>
              <List bordered>
                <List.Item
                  key={betaToggles.shorePayOnline.key}
                  actions={[
                    <Flex align="center" gap={16}>
                      <Text>Try It</Text>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        defaultChecked={
                          hasEnabledbetaToggleAccessShorePayOnlineFeatures
                        }
                        onChange={
                          handleOnBetaToggleAccessShorePayOnlineFeaturesChange
                        }
                      />
                    </Flex>,
                  ]}
                >
                  <Flex vertical>
                    <Text strong>Pay Online (Express Dashboard & Setup)</Text>
                    <Paragraph
                      type="secondary"
                      style={developerStyle.headerParagraph}
                    >
                      Test the new &quot;Pay Online&quot; feature with the
                      Express Dashboard and Setup. Enjoy a faster, more
                      intuitive payment experience with simplified setup and
                      seamless transactions.
                    </Paragraph>
                  </Flex>
                </List.Item>
                <List.Item
                  hidden
                  key={betaToggles.mainMenu.key}
                  actions={[
                    <Flex align="center" gap={16}>
                      <Text>Try It</Text>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        defaultChecked={hasEnabledBetaToggleAccessMainMenu}
                        onChange={handleOnBetaToggleAccessMainMenuChange}
                      />
                    </Flex>,
                  ]}
                >
                  <Flex vertical>
                    <Text strong>Main Menu & Top Header</Text>
                    <Paragraph
                      type="secondary"
                      style={developerStyle.headerParagraph}
                    >
                      Switch to the new menu beta version to test out the the
                      fresh look. This version includes a new layout, improved
                      navigation, and enhanced performance.
                    </Paragraph>
                  </Flex>
                </List.Item>
              </List>
            </Col>
          )}
        </Row>
      </Content>
    </Layout>
  );
}
